/**
 * The **NPC action vocabulary** — the RAW, NPC-specific action catalog a GM drives from the NPC
 * control surface, as a single data-driven table.
 *
 * Sourced from the Lancer Core Book NPC rules (pp. 280–281): an NPC's action set is a
 * **narrower, differently-worded** vocabulary than the PC verbs (NPC Barrage ≠ PC barrage, NPC
 * Stabilize clears only heat/Exposed + reload), *not* a filtered PC list. NPCs **cannot Overcharge
 * or Brace**, and **do not heal or clear conditions when they Stabilize**.
 *
 * This table is the **seam** between the UI and the NPC turn engine: every entry carries a
 * {@link NpcAction.status}. Only the {@link NpcActionStatus `"live"`} ones are wired to a real
 * resolution path; the rest render as honest **"resolves manually — engine pending"** reference
 * rows. As each action's engine lands, flip its `status` to `"live"` and wire it — the table is
 * the single place that record lives.
 *
 * **Pure data** — no I/O, no React. The control surface (`frontend/.../CombatWindows.tsx`) reads
 * it; it must never be re-declared inline in a component.
 */

import type { MechActionRoute } from "./mechActions";

/** An NPC action's action-economy cost class (RAW, pp. 280). Activation/system actions vary, hence not here. */
export type NpcActionCost = "Quick" | "Full" | "Reaction" | "Free" | "Protocol";

/**
 * Whether an action is wired to a real resolution path today.
 * - `"live"` — resolves through an existing engine path (Skirmish → the shared attack-resolution flow).
 * - `"engine-pending"` — RAW-faithful reference only; no resolving engine path yet.
 */
export type NpcActionStatus = "live" | "engine-pending";

/**
 * A short, RAW-grounded classifier for an NPC action — the per-row "category chip" the control
 * surface renders beside the name (the action's *kind*, distinct from its economy `cost`, which the
 * 3-column QUICK/FULL/REACTION grid already conveys). Kept terse + uppercase for the mono chip.
 */
export type NpcActionCategory = "ATTACK" | "MELEE" | "MOVE" | "TECH" | "STEALTH" | "SETUP" | "SUPPORT";

/**
 * The kind of **tier/base-derived value badge** an action carries (the prototype's per-row figure).
 * Resolved to a display string by {@link npcActionValueLabel} from the NPC's tier + speed — never
 * hardcoded per row, never a live-combat number (these are static RAW reference figures):
 * - `"speed"` — Boost: the NPC's Speed (`SPD {speed}`).
 * - `"accuracy"` — Grapple / Ram / Overwatch: the tier accuracy bonus (`+1/2/3` by tier).
 * - `"improvised"` — Improvised Attack: tier accuracy `·` tier damage (`+1·3DMG` … `+3·6DMG`).
 */
export type NpcActionValueKind = "speed" | "accuracy" | "improvised";

/**
 * Tier-indexed accuracy bonus (RAW, Core p. 280): tier 1/2/3 → `+1/+2/+3`. Grapple/Ram/Improvised
 * Attack's flat tier bonus — RAW lists it separately from a weapon's own Accuracy, so `resolve.ts`'s
 * `selectWeapon` reads this SAME table (exported) to stamp it onto those synthetic profiles'
 * `attackBonus` for an NPC attacker, rather than falling
 * back to the attacker's `base.attackBonus` (its weapon Accuracy — a different RAW figure).
 */
export const NPC_ACCURACY_BY_TIER = [1, 2, 3] as const;
/**
 * Tier-indexed Improvised Attack damage (RAW, Core p. 280): tier 1/2/3 → `3/4/6`. Exported so
 * `resolve/weapons.ts` freezes the SAME flat-damage figures onto `act_improvised_attack`'s synthetic profile
 * for an NPC attacker (a `{count:0,sides:0,bonus:N}` profile — the NPC weapon-freeze shape),
 * instead of the PC's rolled 1d6.
 */
export const NPC_IMPROVISED_DMG_BY_TIER = [3, 4, 6] as const;

/**
 * The **one** place NPC action value badges scale off tier/base. Resolves a {@link NpcActionValueKind}
 * to its display string for a given tier + speed (static RAW figures, not live combat math). The tier
 * index is clamped to the 1–3 RAW band.
 */
export function npcActionValueLabel(kind: NpcActionValueKind, ctx: { tier: number; speed: number }): string {
  const i = Math.max(0, Math.min(2, ctx.tier - 1));
  switch (kind) {
    case "speed":
      return `SPD ${ctx.speed}`;
    case "accuracy":
      return `+${NPC_ACCURACY_BY_TIER[i]}`;
    case "improvised":
      return `+${NPC_ACCURACY_BY_TIER[i]}·${NPC_IMPROVISED_DMG_BY_TIER[i]}DMG`;
  }
}

/** One RAW NPC action: a stable id, its RAW name + cost class + category + short blurb + wiring status. */
export type NpcAction = {
  /** Stable key (also the React list key); RAW name lowercased/underscored. */
  id: string;
  /** The RAW action name as printed (e.g. "Skirmish"). */
  name: string;
  /** The action-economy cost class (Quick / Full / Reaction / Free / Protocol) — drives the grid column. */
  cost: NpcActionCost;
  /** The action's kind (ATTACK / MELEE / MOVE / TECH / …) — the per-row chip beside the name. */
  category: NpcActionCategory;
  /** A short RAW blurb of the action's effect (tier figures written `x/y/z`). */
  blurb: string;
  /** Whether it resolves through a live engine path today, or is reference-only. */
  status: NpcActionStatus;
  /**
   * How this action resolves — the SAME {@link MechActionRoute} the unified
   * registry's `ActionDescriptor.route` carries, mirroring `MechAction.route`.
   * `enumerateActions` (`actionRegistry.ts`) tags every NPC core descriptor with this
   * field exactly as it does for a mech's `MECH_ACTIONS`.
   */
  route: MechActionRoute;
  /**
   * Optional tier/base-derived **value badge** kind (the prototype's per-row figure). When set, the
   * control surface renders {@link npcActionValueLabel}(value, {tier, speed}); when absent, no badge.
   */
  value?: NpcActionValueKind;
};

/**
 * The RAW NPC action catalog (Core Book pp. 280–281). Ordered Skirmish-first (the live attack), then
 * the remaining quick actions, the full actions, and the default reaction. Quick Tech / Full Tech and
 * the tier-scaled figures (`+1/2/3`, `3/4/6`) are written as RAW; the tier resolves at the NPC's tier.
 */
export const NPC_ACTIONS: readonly NpcAction[] = [
  {
    id: "skirmish",
    name: "Skirmish",
    cost: "Quick",
    category: "ATTACK",
    blurb: "Attack with one Auxiliary, Main, or Heavy weapon.",
    status: "live",
    route: "attack",
  },
  {
    id: "boost",
    name: "Boost",
    cost: "Quick",
    category: "MOVE",
    blurb: "Move a number of spaces equal to Speed.",
    status: "live",
    route: "reducer",
    value: "speed",
  },
  {
    id: "grapple",
    name: "Grapple",
    cost: "Quick",
    category: "MELEE",
    blurb: "Melee attack vs. an adjacent character, +1/2/3; grapples on a success.",
    status: "live",
    route: "attack",
    value: "accuracy",
  },
  {
    id: "ram",
    name: "Ram",
    cost: "Quick",
    category: "MELEE",
    blurb: "Melee vs. an adjacent character of their size or smaller, +1/2/3; on success knock Prone and optionally 1 space back.",
    status: "live",
    route: "attack",
    value: "accuracy",
  },
  {
    id: "hide",
    name: "Hide",
    cost: "Quick",
    category: "STEALTH",
    blurb: "The NPC becomes Hidden.",
    status: "live",
    route: "reducer",
  },
  {
    id: "search",
    name: "Search",
    cost: "Quick",
    category: "TECH",
    blurb: "Contested Systems vs. the Agility of a Hidden character within Sensors; on success the target ceases to be Hidden.",
    status: "live",
    route: "reducer",
  },
  {
    id: "quick_tech",
    name: "Quick Tech",
    cost: "Quick",
    category: "TECH",
    blurb: "Invade (tech attack vs. E-DEF: 2 heat + Impaired) or Lock On, or a Quick Tech option from the NPC's profile.",
    status: "live",
    route: "activate",
  },
  {
    id: "prepare",
    name: "Prepare",
    cost: "Quick",
    category: "SETUP",
    blurb: "Prepare a quick action to take as a reaction (player Prepare rules).",
    status: "live",
    route: "reducer",
  },
  {
    id: "barrage",
    name: "Barrage",
    cost: "Full",
    category: "ATTACK",
    blurb: "Attack with up to two Auxiliary/Main/Heavy weapons, or one Superheavy; may split across targets.",
    status: "live",
    route: "attack",
  },
  {
    id: "improvised_attack",
    name: "Improvised Attack",
    cost: "Full",
    category: "MELEE",
    blurb: "Melee attack, +1/2/3, dealing 3/4/6 damage by tier.",
    status: "live",
    route: "attack",
    value: "improvised",
  },
  {
    id: "disengage",
    name: "Disengage",
    cost: "Full",
    category: "MOVE",
    blurb: "For the rest of the turn, ignore Engagement; movement doesn't provoke reactions.",
    status: "live",
    route: "reducer",
  },
  {
    id: "full_tech",
    name: "Full Tech",
    cost: "Full",
    category: "TECH",
    blurb: "A Full Tech option from the profile, or two Quick Tech actions (may repeat the same one).",
    status: "live",
    route: "activate",
  },
  {
    id: "stabilize",
    name: "Stabilize",
    cost: "Full",
    category: "SUPPORT",
    blurb: "Clears all heat and the Exposed status and reloads Loading weapons. Does NOT heal HP or clear other conditions.",
    status: "live",
    route: "reducer",
  },
  {
    id: "overwatch",
    name: "Overwatch",
    cost: "Reaction",
    category: "ATTACK",
    blurb: "When a character starts moving within threat range, take Skirmish against them as a reaction (once between turns). NPCs cannot Brace.",
    status: "live",
    // "attack", not "reaction": this row is a GM-DECLARED dispatch (arm → click a target → fire NOW,
    // riding the `reaction: true` attack path), not the Category-B informational row
    // `MechActionRoute`'s `"reaction"` means (a granted reaction rendered but never entered into
    // the activate list). The movement-TRIGGERED automatic prompt stays the automatic-reaction
    // system's, for PCs and NPCs alike; this route only concerns the GM's proactive button.
    route: "attack",
    value: "accuracy",
  },
];
